import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import {
	Box,
	Button,
	CircularProgress,
	InputAdornment,
	TextField,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import {
	getEscapeRooms,
	getButtonTemplateCatalog,
	setButtonCatalogSource,
} from "../../../../api/escape-room";
import { alertError, alertSuccess } from "../../../../utils/logger";

function ownerLabel(er) {
	const owner = er?.createdBy;
	if (!owner || typeof owner !== "object") return "—";
	const name = String(owner.fullName || "").trim();
	const email = String(owner.email || "").trim();
	if (name && email) return `${name} · ${email}`;
	return name || email || "—";
}

export default function ButtonCatalogSourcePage() {
	const history = useHistory();
	const mountedRef = useRef(true);
	const [current, setCurrent] = useState({ escapeRoomId: null, escapeRoomName: "", itemCount: 0 });
	const [loadingCurrent, setLoadingCurrent] = useState(true);
	const [nameQuery, setNameQuery] = useState("");
	const [userQuery, setUserQuery] = useState("");
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [savingId, setSavingId] = useState("");

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const refreshCurrent = useCallback(() => {
		if (mountedRef.current) setLoadingCurrent(true);
		return getButtonTemplateCatalog()
			.then((res) => {
				if (!mountedRef.current) return;
				const data = res?.data || {};
				setCurrent({
					escapeRoomId: data.escapeRoomId ? String(data.escapeRoomId) : null,
					escapeRoomName: String(data.escapeRoomName || "").trim(),
					itemCount: Array.isArray(data.items) ? data.items.length : 0,
				});
			})
			.catch((err) => {
				if (!mountedRef.current) return;
				alertError({ error: err, customMessage: "No se pudo cargar el catálogo de botones actual." });
			})
			.finally(() => {
				if (mountedRef.current) setLoadingCurrent(false);
			});
	}, []);

	const runSearch = useCallback((opts = {}) => {
		if (mountedRef.current) setLoading(true);
		return getEscapeRooms({
			name: opts.name ?? nameQuery,
			user: opts.user ?? userQuery,
			limit: 80,
		})
			.then((res) => {
				if (!mountedRef.current) return;
				setItems(Array.isArray(res.data) ? res.data : []);
			})
			.catch((err) => {
				if (!mountedRef.current) return;
				setItems([]);
				alertError({ error: err, customMessage: "No se pudo buscar escape rooms." });
			})
			.finally(() => {
				if (mountedRef.current) setLoading(false);
			});
	}, [nameQuery, userQuery]);

	useEffect(() => {
		let cancelled = false;
		setLoadingCurrent(true);
		setLoading(true);
		Promise.all([
			getButtonTemplateCatalog()
				.then((res) => {
					if (cancelled || !mountedRef.current) return;
					const data = res?.data || {};
					setCurrent({
						escapeRoomId: data.escapeRoomId ? String(data.escapeRoomId) : null,
						escapeRoomName: String(data.escapeRoomName || "").trim(),
						itemCount: Array.isArray(data.items) ? data.items.length : 0,
					});
				})
				.catch((err) => {
					if (cancelled || !mountedRef.current) return;
					alertError({ error: err, customMessage: "No se pudo cargar el catálogo de botones actual." });
				})
				.finally(() => {
					if (!cancelled && mountedRef.current) setLoadingCurrent(false);
				}),
			getEscapeRooms({ name: "", user: "", limit: 80 })
				.then((res) => {
					if (cancelled || !mountedRef.current) return;
					setItems(Array.isArray(res.data) ? res.data : []);
				})
				.catch((err) => {
					if (cancelled || !mountedRef.current) return;
					setItems([]);
					alertError({ error: err, customMessage: "No se pudo buscar escape rooms." });
				})
				.finally(() => {
					if (!cancelled && mountedRef.current) setLoading(false);
				}),
		]);
		return () => {
			cancelled = true;
		};
	}, []);

	const emptyMessage = useMemo(() => {
		if (loading) return null;
		if (nameQuery.trim() || userQuery.trim()) {
			return "No hay escape rooms que coincidan con la búsqueda.";
		}
		return "No hay escape rooms todavía.";
	}, [loading, nameQuery, userQuery]);

	const handleUseAsCatalog = (id) => {
		const sid = String(id || "").trim();
		if (!sid || savingId) return;
		setSavingId(sid);
		setButtonCatalogSource(sid)
			.then((res) => {
				if (!mountedRef.current) return;
				if (res.status === 200 && res.data) {
					alertSuccess({
						customMessage:
							"Catálogo de botones actualizado. Este escape room queda protegido frente a borrado.",
					});
					setCurrent({
						escapeRoomId: String(res.data._id),
						escapeRoomName: String(res.data.name || "").trim(),
						itemCount: current.itemCount,
					});
					refreshCurrent();
					runSearch();
				}
			})
			.catch((err) => {
				if (!mountedRef.current) return;
				alertError({ error: err, customMessage: "No se pudo marcar como catálogo de botones." });
			})
			.finally(() => {
				if (mountedRef.current) setSavingId("");
			});
	};

	return (
		<>
			<Card>
				<CardHeader title="Catálogo de botones del editor" />
				<CardBody>
					<Box fontSize={14} color="#64748b" lineHeight={1.55} mb={2}>
						Elige qué escape room alimenta las plantillas del panel <strong>Elementos → Botón</strong> del
						portal. Solo puede haber uno activo y <strong>no se puede eliminar</strong> mientras esté marcado.
					</Box>

					{loadingCurrent ? (
						<Box display="flex" alignItems="center" style={{ gap: 10 }}>
							<CircularProgress size={22} />
							<span style={{ color: "#64748b" }}>Cargando fuente actual…</span>
						</Box>
					) : (
						<Box
							style={{
								padding: 16,
								borderRadius: 14,
								border: "1px solid #ddd6fe",
								background: "#faf5ff",
							}}
						>
							<div style={{ fontWeight: 800, color: "#5b21b6", marginBottom: 6 }}>Fuente activa</div>
							{current.escapeRoomId ? (
								<>
									<div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
										{current.escapeRoomName || "Sin nombre"}
									</div>
									<div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
										{current.itemCount} plantilla{current.itemCount === 1 ? "" : "s"} de botón · id{" "}
										<code style={{ fontSize: 12 }}>{current.escapeRoomId}</code>
									</div>
									<Box mt={1.5} display="flex" style={{ gap: 10 }} flexWrap="wrap">
										<Button
											variant="outlined"
											color="primary"
											onClick={() => history.push(`/escape-rooms/${current.escapeRoomId}`)}
											style={{ textTransform: "none", fontWeight: 700 }}
										>
											Abrir en el editor
										</Button>
									</Box>
								</>
							) : (
								<div style={{ color: "#64748b" }}>
									Todavía no hay un escape room marcado. Busca uno abajo y pulsa{" "}
									<strong>Usar como catálogo</strong>.
								</div>
							)}
						</Box>
					)}
				</CardBody>
			</Card>

			<Card className="mt-4">
				<CardHeader title="Elegir otro escape room" />
				<CardBody>
					<Box display="flex" flexWrap="wrap" style={{ gap: 12, marginBottom: 16 }}>
						<TextField
							label="Nombre del escape room"
							variant="outlined"
							size="small"
							value={nameQuery}
							onChange={(e) => {
								const v = e.target.value;
								setNameQuery(v);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									runSearch();
								}
							}}
							style={{ minWidth: 240, flex: "1 1 240px" }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon style={{ color: "#94a3b8", fontSize: 20 }} />
									</InputAdornment>
								),
							}}
						/>
						<TextField
							label="Usuario (email o nombre)"
							variant="outlined"
							size="small"
							value={userQuery}
							onChange={(e) => {
								const v = e.target.value;
								setUserQuery(v);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									runSearch();
								}
							}}
							style={{ minWidth: 240, flex: "1 1 240px" }}
						/>
						<Button
							variant="contained"
							color="primary"
							onClick={() => runSearch()}
							disabled={loading}
							style={{ alignSelf: "center", minHeight: 40 }}
						>
							{loading ? "Buscando…" : "Buscar"}
						</Button>
					</Box>

					{loading ? (
						<Box display="flex" justifyContent="center" py={4}>
							<CircularProgress size={32} />
						</Box>
					) : items.length ? (
						<Box display="flex" flexDirection="column" style={{ gap: 10 }}>
							{items.map((er) => {
								const isActive = current.escapeRoomId && String(er._id) === current.escapeRoomId;
								const flagged = Boolean(er.buttonCatalogSource) || isActive;
								return (
									<Box
										key={er._id}
										display="flex"
										alignItems="center"
										justifyContent="space-between"
										flexWrap="wrap"
										style={{
											gap: 12,
											padding: "12px 14px",
											borderRadius: 12,
											border: flagged ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
											background: flagged ? "#faf5ff" : "#fff",
										}}
									>
										<Box minWidth={0} flex="1 1 220px">
											<div style={{ fontWeight: 800, color: "#0f172a" }}>{er.name || "Sin nombre"}</div>
											<div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{ownerLabel(er)}</div>
										</Box>
										<Box display="flex" alignItems="center" style={{ gap: 8 }} flexWrap="wrap">
											{flagged ? (
												<span
													style={{
														fontSize: 12,
														fontWeight: 800,
														color: "#5b21b6",
														background: "#ede9fe",
														padding: "6px 10px",
														borderRadius: 8,
													}}
												>
													Fuente activa
												</span>
											) : (
												<Button
													variant="contained"
													color="primary"
													disabled={Boolean(savingId)}
													onClick={() => handleUseAsCatalog(er._id)}
													style={{ textTransform: "none", fontWeight: 700 }}
												>
													{savingId === String(er._id) ? "Guardando…" : "Usar como catálogo"}
												</Button>
											)}
											<Button
												variant="outlined"
												onClick={() => history.push(`/escape-rooms/${er._id}`)}
												style={{ textTransform: "none", fontWeight: 700 }}
											>
												Abrir
											</Button>
										</Box>
									</Box>
								);
							})}
						</Box>
					) : (
						<Box
							py={4}
							px={2}
							textAlign="center"
							style={{ color: "#64748b", background: "#f8fafc", borderRadius: 12, border: "1px dashed #cbd5e1" }}
						>
							{emptyMessage}
						</Box>
					)}
				</CardBody>
			</Card>
		</>
	);
}
